import { X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import UpgradeToPremiumButton from './UpgradeToPremiumButton';

interface RealmLimitExceededPopupProps {
  isOpen: boolean;
  onClose?: () => void;
}

const RealmLimitExceededPopup: React.FC<RealmLimitExceededPopupProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleGoToConfig = () => {
    navigate('/settings/realm');
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 z-5555 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 bg-fp-bg border border-fp-border rounded-lg shadow-2xl max-w-md w-full mx-4 p-6">
        {/* Close button (optional) */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors"
            aria-label={t('buttons.close')}
          >
            <X size={20} />
          </button>
        )}

        {/* Icon/Warning */}
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-white text-center mb-2">
          {t('realmLimitPopup.title')}
        </h2>

        {/* Message */}
        <p className="text-gray-300 text-center mb-6">
          {t('realmLimitPopup.description')}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleGoToConfig}
            className="w-full px-4 py-2.5 bg-fp-border hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
          >
            {t('buttons.goToConfig')}
          </button>

          <UpgradeToPremiumButton
            className="w-full justify-center"
          />
        </div>
      </div>
    </div>
  );
};

export default RealmLimitExceededPopup;
